import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, History, Pencil, Trash2 } from "lucide-react";
import { openToast } from "@utils/toastStore";
import { ServicesURL } from "@utils/utilsApi";
import { PageFilterField } from "@hooks/usePageFilters";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import EnrollmentListShell from "../components/EnrollmentListShell";
import EnrollmentHistoryModal from "../components/EnrollmentHistoryModal";
import EditEnrollmentModal from "../components/EditEnrollmentModal";
import WaitingUserRow from "../components/WaitingUserRow";
import { useWaitingUsersList } from "../hooks/useWaitingUsersList";
import { useApproveOrDeclineEnrollment } from "../hooks/useApproveOrDeclineEnrollment";
import { EnrolledUserItem, WaitingUserItem } from "../types";

const PAGE_SIZE = 10;

const WAITING_FILTER_FIELDS: PageFilterField[] = [
  {
    key: "userId",
    accessorKey: "userId",
    type: "text",
    headerLabels: { it: "ID", en: "ID" },
  },
  {
    key: "massiveGroupId",
    accessorKey: "massiveGroupId",
    type: "text",
    headerLabels: { it: "ID Gruppo", en: "Group ID" },
  },
  {
    key: "userName",
    accessorKey: "userName",
    type: "text",
    headerLabels: { it: "Nome", en: "First name" },
  },
  {
    key: "userSurname",
    accessorKey: "userSurname",
    type: "text",
    headerLabels: { it: "Cognome", en: "Last name" },
  },
  {
    key: "userEmail",
    accessorKey: "userEmail",
    type: "text",
    headerLabels: { it: "Email", en: "Email" },
  },
];

const toEnrolledUserItem = (user: WaitingUserItem): EnrolledUserItem => ({
  userId: user.userId,
  userFirstName: user.userName,
  userLastName: user.userSurname,
  userEmail: user.userEmail,
  enrollType: user.enrollType,
  status: user.status,
  enrollId: user.enrollId,
  editionId: user.editionId,
  completeDate: user.completeDate,
  expirationDate: user.expirationDate,
  mandatory: user.mandatory,
  massiveGroupId: user.massiveGroupId,
  isExpirationHard: user.isExpirationHard,
  isExpirationFixed: user.isExpirationFixed,
  daysToComplete: user.daysToComplete,
  attributes: user.attributes ?? user.attributesIds,
});

interface WaitingUsersTabProps {
  corporateId: string | number;
  objectId: number;
  objectType: CatalogEntityType;
  objectTitle?: string;
  editionId?: number;
  onChange?: () => void;
}

const WaitingUsersTab: React.FC<WaitingUsersTabProps> = ({
  corporateId,
  objectId,
  objectType,
  objectTitle,
  editionId,
  onChange,
}) => {
  const { t } = useTranslation("users-and-groups");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});
  const [pendingId, setPendingId] = useState<string | number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EnrolledUserItem | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<EnrolledUserItem | null>(
    null,
  );
  const [editTarget, setEditTarget] = useState<EnrolledUserItem | null>(null);

  const { items, totalItems, totalPages, isLoading, mutate } =
    useWaitingUsersList({
      corporateId,
      objectId,
      editionId,
      page,
      pageSize,
      search: search || undefined,
      fieldFilters,
    });

  const refresh = () => {
    mutate();
    onChange?.();
  };

  const { submit, isMutating } = useApproveOrDeclineEnrollment();
  const { can } = usePermissions();
  // D02 (decisione: allineare V2 a v1): Approva / Rifiuta con
  // learning_object.write, come users-waiting-config.json di v1.
  const canApproveOrDecline = can(PERMISSIONS.catalog.write);
  const canUpdateEnrollment = can(PERMISSIONS.enrollment.update);

  const handleApproveToggle = async (
    userId: string | number,
    approve: boolean,
  ) => {
    if (!userId || !canApproveOrDecline) return;
    setPendingId(userId);
    try {
      await submit({
        studentEid: userId,
        corporateId: Number(corporateId),
        objectId,
        editionId,
        approve,
      });
      openToast({
        severity: "success",
        message: String(
          t(approve ? "approve.success-approved" : "approve.success-declined"),
        ),
      });
      refresh();
    } catch (e: any) {
      openToast({
        severity: "error",
        message: e?.message || String(t("approve.error")),
      });
    } finally {
      setPendingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!canUpdateEnrollment || !deleteTarget?.enrollId) return;
    setIsDeleting(true);
    try {
      await fetcher(
        `${ServicesURL.deleteEnrollment}?enrollmentEId=${deleteTarget.enrollId}`,
        MethodType.DELETE,
      );
      setDeleteTarget(null);
      refresh();
      openToast({
        severity: "success",
        message: String(t("card.delete-confirm.success")),
      });
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("card.delete-confirm.error")),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const buildMoreActions = (user: WaitingUserItem): ListItemAction[][] => [
    [
      {
        label: String(t("card.actions.edit-enrollment")),
        icon: <Pencil size={14} />,
        onClick: () => setEditTarget(toEnrolledUserItem(user)),
        disabled: !canUpdateEnrollment || !user.userId,
      },
      {
        label: String(t("card.actions.delete-enrollment")),
        icon: <Trash2 size={14} />,
        onClick: () => setDeleteTarget(toEnrolledUserItem(user)),
        disabled: !canUpdateEnrollment || user.enrollId == null,
      },
    ],
    [
      {
        label: String(t("card.actions.enrollment-history")),
        icon: <History size={14} />,
        onClick: () => setHistoryTarget(toEnrolledUserItem(user)),
        disabled: !user.userId,
      },
    ],
    [
      {
        label: String(t("card.actions.download-certificate")),
        icon: <Download size={14} />,
        onClick: () => undefined,
        disabled: true,
      },
    ],
  ];

  const deleteTargetName = deleteTarget
    ? [deleteTarget.userFirstName, deleteTarget.userLastName]
        .filter(Boolean)
        .join(" ")
        .trim() || "-"
    : "";

  return (
    <EnrollmentListShell
      search={search}
      onSearchSubmit={(v) => {
        setSearch(v);
        setPage(1);
      }}
      hideSearch
      filtersExtraFields={WAITING_FILTER_FIELDS}
      fieldFilters={fieldFilters}
      onApplyFilters={(next) => {
        setFieldFilters(next);
        setPage(1);
      }}
      isLoading={isLoading}
      isEmpty={items.length === 0}
      emptyLabel={String(t("empty.waiting-users"))}
      page={page}
      pageSize={pageSize}
      totalItems={totalItems}
      totalPages={totalPages}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
    >
      {items.map((user) => (
        <WaitingUserRow
          key={`${user.userId}-${user.objectId ?? ""}`}
          user={user}
          approved={false}
          disabled={
            !canApproveOrDecline || (isMutating && pendingId === user.userId)
          }
          onApproveToggle={(next) =>
            user.userId && handleApproveToggle(user.userId, next)
          }
          moreActions={buildMoreActions(user)}
          // TODO: riattivare la freccia quando esisterà il detail enrollment
          // onArrowClick={(u) => navigate(...)}
        />
      ))}

      <ConfirmDialogCst
        open={!!deleteTarget}
        title={String(t("card.delete-confirm.title"))}
        description={String(
          t("card.delete-confirm.description", { name: deleteTargetName }),
        )}
        confirmLabel={String(t("card.delete-confirm.confirm"))}
        cancelLabel={String(t("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <EnrollmentHistoryModal
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        objectId={objectId}
        objectTitle={objectTitle}
        user={historyTarget}
      />

      <EditEnrollmentModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        corporateId={corporateId}
        objectId={objectId}
        objectType={objectType}
        objectTitle={objectTitle}
        user={editTarget}
        onSuccess={refresh}
      />
    </EnrollmentListShell>
  );
};

export default WaitingUsersTab;
